import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import CheckoutForm from "./components/order/Checkout";
import Gallery from "./components/gallery/Gallery";
import ItemPage from "./components/gallery/ItemPage";
import LoginForm from "./components/login/LoginForm";
import NotFound from "./components/notfound/NotFound";
import OrderForm from "./components/order/Order";

import Badge from "@material-ui/core/Badge";
import CircularProgress from "@material-ui/core/CircularProgress";
import ThemeProvider from "@material-ui/styles/ThemeProvider";
import { createTheme } from "@material-ui/core/styles";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import "./App.css";
import logo from "./logo.png";

const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_80EF3UNJD7lLyz9CJxVMeRQG00nA5a1TNk";
const stripePromise = loadStripe(stripePublishableKey);

const AppShell = () => {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const loggedIn = Boolean(session && session.userId);
  const onLoginPage = location.pathname === "/login";
  const showTopChrome = loggedIn || !onLoginPage;

  const incNumItems = () => {
    setSession({
      ...session,
      numItems: session.numItems + 1,
    });
  };
  const wipeBadge = () => {
    setSession({
      ...session,
      numItems: 0,
    });
  };

  useEffect(() => {
    if (!session) {
      const fetchIdentity = async () => {
        setIsLoading(true);
        await fetch("/api/auth/identity")
          .then((res) => res.json())
          .then((data) => setSession(data));
        setIsLoading(false);
      };

      fetchIdentity();
    }
  }, [session]);

  return (
      <div
        className={
          onLoginPage && !loggedIn ? "siteShell siteShell--login" : "siteShell"
        }
      >
        <img alt="logo" className="logo" src={logo} />
        <ThemeProvider
        theme={createTheme({
          spacing: 4,
          typography: {
            fontFamily: ['"Avenir Next"'].join(","),
            h1: {
              letterSpacing: "0.35em",
            },
            h2: {
              fontWeight: "400",
              letterSpacing: "0.12em",
            },
            h3: {
              fontSize: "1.35rem",
              fontWeight: "400",
              letterSpacing: "0.06em",
            },
            button: {
              fontWeight: "700",
              letterSpacing: "2px",
            },
          },
          palette: {
            action: {
              active: "#e9a6b8",
            },
            background: {
              default: "#1a1214",
              paper: "#2a2024",
            },
            error: {
              main: "#e86868",
            },
            text: {
              primary: "#f3ebe3",
              secondary: "rgba(243, 235, 227, 0.72)",
            },
            primary: {
              main: "#e9a6b8",
              contrastText: "#1a1214",
            },
            secondary: {
              main: "#6b3d4a",
              contrastText: "#f3ebe3",
            },
          },
        })}
      >
        {showTopChrome && (
          <div className="bannerContainer">
            <div className="bannerText">
              Buy 1-4 images for $12 each, or 5+ images for $9.50 each
            </div>
          </div>
        )}
        {isLoading ? (
          <CircularProgress />
        ) : (
          <React.Fragment>
            {showTopChrome && (
            <div className="nav">
              <div className="navInner">
                <a
                  className="navElement"
                  onClick={() => (window.location = "/gallery")}
                >
                  GALLERY
                </a>
                {session && session.userId && (
                  <Badge
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    badgeContent={session.numItems}
                    className="navCartBadge"
                    color="primary"
                    max={100}
                    onClick={() => (window.location = "/order")}
                  >
                    <span className="navElement navCartLabel">CART</span>
                  </Badge>
                )}
                {!(session && session.userId) ? (
                  <a
                    className="navElement"
                    onClick={() => (window.location = "/login")}
                  >
                    LOGIN
                  </a>
                ) : (
                  <a
                    className="navElement"
                    onClick={async () => {
                      setIsLoading(true);
                      await fetch("/api/auth/logout", {
                        method: "POST",
                      })
                        .then((res) => res.json())
                        .then(() => setSession(null));
                      setIsLoading(false);
                    }}
                  >
                    LOG OUT
                  </a>
                )}
              </div>
            </div>
            )}
            <div className="page">
              <Routes>
                <Route
                  exact
                  path="/"
                  element={<Navigate replace to={{ pathname: "/gallery" }} />}
                />
                <Route element={<LoginForm />} path="/login" />
                <Route
                  path="/gallery"
                  element={
                    session && session.userId ? (
                      <Gallery />
                    ) : (
                      <Navigate replace to={{ pathname: "/login" }} />
                    )
                  }
                />
                <Route
                  path="/gallery/:galleryId"
                  element={
                    session && session.userId ? (
                      <Gallery />
                    ) : (
                      <Navigate replace to={{ pathname: "/login" }} />
                    )
                  }
                />
                <Route
                  path="/item/:itemId"
                  element={
                    session && session.userId ? (
                      <ItemPage incNumItems={incNumItems} />
                    ) : (
                      <Navigate replace to={{ pathname: "/login" }} />
                    )
                  }
                />
                <Route
                  path="/order"
                  element={
                    session && session.userId ? (
                      <OrderForm />
                    ) : (
                      <Navigate replace to={{ pathname: "/login" }} />
                    )
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    session && session.userId ? (
                      <Elements stripe={stripePromise}>
                        <CheckoutForm wipeBadge={wipeBadge} />
                      </Elements>
                    ) : (
                      <Navigate replace to={{ pathname: "/login" }} />
                    )
                  }
                />
                <Route component={NotFound} />
              </Routes>
            </div>
          </React.Fragment>
        )}
        </ThemeProvider>
      </div>
  );
};

const App = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default App;
